import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./ClubMembers.module.scss";

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "member", label: "Member" },
  { value: "host", label: "Host" },
  { value: "partner", label: "Partner" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "removed", label: "Removed" },
  { value: "banned", label: "Banned" },
];

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

const ClubMembers = () => {
  const { cityId, streetId, clubId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  // server filters
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");

  // client search
  const [q, setQ] = useState("");

  const base = useMemo(() => {
    return `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`;
  }, [cityId, streetId, clubId]);

  const backToClubPath = useMemo(() => {
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}`;
  }, [cityId, streetId, clubId]);

  const viewMembershipLink = useCallback(
    (membershipId) =>
      `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/memberships/${membershipId}`,
    [cityId, streetId, clubId]
  );

  const fetchMembers = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setErr("");
        setMsg("");

        const { data } = await api.get(`${base}/members`, {
          params: {
            page,
            limit: meta.limit,
            role: role || undefined,
            status: status || undefined,
          },
        });

        setMembers(Array.isArray(data?.members) ? data.members : []);
        setMeta(
          data?.meta || { page: 1, limit: meta.limit, total: 0, pages: 1 }
        );
      } catch (e) {
        setErr(
          e?.response?.data?.message || e?.message || "Failed to load members."
        );
        setMembers([]);
        setMeta({ page: 1, limit: meta.limit, total: 0, pages: 1 });
      } finally {
        setLoading(false);
      }
    },
    [base, role, status, meta.limit]
  );

  useEffect(() => {
    fetchMembers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, status]);

  useEffect(() => {
    fetchMembers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = String(q || "")
      .trim()
      .toLowerCase();
    if (!term) return members;

    return members.filter((m) => {
      const uname = (m?.username || "").toLowerCase();
      const role = (m?.membershipRole || "").toLowerCase();
      const st = (m?.membershipStatus || "").toLowerCase();
      return uname.includes(term) || role.includes(term) || st.includes(term);
    });
  }, [members, q]);

  const onPrev = () => fetchMembers(clamp(meta.page - 1, 1, meta.pages));
  const onNext = () => fetchMembers(clamp(meta.page + 1, 1, meta.pages));
  const onGo = (p) => fetchMembers(clamp(p, 1, meta.pages));

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <Link to={backToClubPath} className={cls.backLink}>
            ← Back to Club
          </Link>

          <p className={cls.eyebrow}>Admin · Club</p>
          <h1 className={cls.title}>All Members</h1>
          <p className={cls.sub}>
            Browse everyone in this club. Use filters and open a membership to
            view details.
          </p>
        </div>

        <div className={cls.headerRight}>
          <button
            type="button"
            className={`nu-btn nu-btn--ghost ${cls.refreshBtn}`}
            onClick={() => fetchMembers(meta.page)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      {err && <div className={cls.alertErr}>⚠ {err}</div>}
      {msg && <div className={cls.alertOk}>{msg}</div>}

      <section className={cls.toolbar}>
        <div className={cls.searchWrap}>
          <input
            className={cls.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search username / role / status…"
          />
        </div>

        <div className={cls.filters}>
          <select
            className={cls.select}
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            className={cls.select}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className={cls.metaPill}>
          <span className={cls.metaStrong}>{meta.total}</span> total
        </div>
      </section>

      <section className={cls.card}>
        <div className={cls.cardHead}>
          <div>
            <h2 className={cls.cardTitle}>Members</h2>
            <p className={cls.cardSub}>
              Page {meta.page} of {meta.pages}
            </p>
          </div>

          <div className={cls.pager}>
            <button
              type="button"
              className={`nu-btn nu-btn--ghost ${cls.pagerBtn}`}
              onClick={onPrev}
              disabled={loading || meta.page <= 1}
            >
              Prev
            </button>

            <div className={cls.pageInfo}>
              {meta.page} / {meta.pages}
            </div>

            <button
              type="button"
              className={`nu-btn nu-btn--ghost ${cls.pagerBtn}`}
              onClick={onNext}
              disabled={loading || meta.page >= meta.pages}
            >
              Next
            </button>
          </div>
        </div>

        {!filtered.length ? (
          <div className={cls.empty}>
            {q.trim()
              ? "No results for your search."
              : "No members found in this club."}
          </div>
        ) : (
          <div className={cls.list}>
            {filtered.map((m) => {
              const avatarUrl = m?.avatar?.url || "";
              const username = m?.username || "unknown";
              const userStatus = m?.userStatus || "—";
              const membershipRole = m?.membershipRole || "—";
              const membershipStatus = m?.membershipStatus || "—";
              const membershipId = m?.membershipId;

              return (
                <div key={m.membershipId} className={cls.row}>
                  <div className={cls.avatarWrap}>
                    {avatarUrl ? (
                      <img
                        className={cls.avatar}
                        src={avatarUrl}
                        alt={username}
                        loading="lazy"
                      />
                    ) : (
                      <div className={cls.avatarFallback} />
                    )}
                  </div>

                  <div className={cls.rowMain}>
                    <div className={cls.rowTop}>
                      <div className={cls.username}>@{username}</div>
                      <div className={cls.pills}>
                        <span className={cls.pill}>role: {membershipRole}</span>
                        <span className={cls.pill}>
                          club: {membershipStatus}
                        </span>
                        <span className={cls.pillMuted}>
                          user: {userStatus}
                        </span>
                      </div>
                    </div>

                    <div className={cls.rowMeta}>
                      <span className={cls.metaKey}>User ID</span>
                      <span className={cls.metaVal}>{m?.userId || "—"}</span>
                      <span className={cls.dot} />
                      <span className={cls.metaKey}>Member ID</span>
                      <span className={cls.metaVal}>{m?.memberId || "—"}</span>
                    </div>
                  </div>

                  <div className={cls.rowActions}>
                    {membershipId ? (
                      <Link
                        className={`nu-btn nu-btn--blue ${cls.viewBtn}`}
                        to={viewMembershipLink(membershipId)}
                      >
                        View
                      </Link>
                    ) : (
                      <button className={`nu-btn ${cls.viewBtn}`} disabled>
                        View
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={cls.cardFoot}>
          <div className={cls.footLeft}>
            Showing <strong>{filtered.length}</strong> on this page
          </div>

          <div className={cls.footRight}>
            <button
              type="button"
              className={`nu-btn nu-btn--ghost ${cls.pagerBtn}`}
              onClick={() => onGo(1)}
              disabled={loading || meta.page === 1}
            >
              First
            </button>
            <button
              type="button"
              className={`nu-btn nu-btn--ghost ${cls.pagerBtn}`}
              onClick={() => onGo(meta.pages)}
              disabled={loading || meta.page === meta.pages}
            >
              Last
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClubMembers;
